import { useEffect, useRef, useState } from "react";
import { Alert, Box, Chip, CircularProgress, Divider, IconButton, InputAdornment, List, ListItemButton, ListSubheader, Menu, MenuItem, Paper, TablePagination, TextField, Typography } from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import FilterListOutlined from "@mui/icons-material/FilterListOutlined";
import dayjs from "dayjs";
import PpLogo from "../../components/common/PpLogo";
import { formatCurrency } from "../../utils/formatCurrency";
import { useCompanyId } from "../../hooks/useCompanyId";
import { QUEUE_LABELS, SCOPE_LABELS, TAB_LABELS, formatTimeLeft, formatWaiting, paypalStatusLabel, reasonLabel, timeLeftColor } from "./labels";
import { INQUIRY_QUEUE_FILTERS, INQUIRY_SCOPES, InquiryAuthor, InquiryCounts, InquiryListItem, InquiryQueueFilter, InquiryScope } from "./types";
import { INK, PROOF_COLORS } from "./styles";

const TABS = Object.keys(TAB_LABELS) as (keyof typeof TAB_LABELS)[];

const MENU_HEADER = { fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', color: INK.muted, lineHeight: '24px', px: '12px' } as const;

const MENU_ITEM = { fontSize: 13, justifyContent: 'space-between', '&.Mui-selected, &.Mui-selected:hover': { bgcolor: INK.tint, color: INK.primary, fontWeight: 600 } } as const;

const ELLIPSIS = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } as const;

const PILL = { flexShrink: 0, whiteSpace: 'nowrap', height: 16, lineHeight: '16px', px: '6px', borderRadius: '8px', fontSize: 10, fontWeight: 600 } as const;

const AUTHOR_PREFIX: Record<InquiryAuthor, string> = { BUYER: 'Buyer', SELLER: 'Us', ARBITER: 'PayPal' };

function countOf(choice: InquiryQueueFilter | InquiryScope, counts: InquiryCounts) {
  if (!(choice in counts)) return null;
  const count = counts[choice as keyof InquiryCounts];
  return count > 99 ? '99+' : String(count);
}

function MenuCount({ count }: { count: string | null }) {
  return <Box component="span" sx={{ color: INK.muted, fontWeight: 400 }}>{count && ` ${count}`}</Box>;
}

const CHIP_COLORS = {
  red: { bgcolor: '#fef3f2', color: '#b42318' },
  amber: { bgcolor: '#fffaeb', color: '#b54708' },
  grey: { bgcolor: '#f2f4f7', color: '#475467' },
} as const;

function statusChipColors(item: InquiryListItem) {
  if (item.queue === 'needs_reply') return CHIP_COLORS.red;
  if (item.status === 'WAITING_FOR_BUYER_RESPONSE' || item.status === 'UNDER_REVIEW') return CHIP_COLORS.amber;
  return CHIP_COLORS.grey;
}

export default function InquiryQueue({
  items, counts, queue, scope, onFilters, search, onSearch, selectedId, onSelect, page, total, onPage, loading, error,
}: {
  items: InquiryListItem[];
  counts: InquiryCounts;
  queue: InquiryQueueFilter;
  scope: InquiryScope;
  onFilters: (next: { queue?: InquiryQueueFilter; scope?: InquiryScope }) => void;
  search: string;
  onSearch: (search: string) => void;
  selectedId: number | null;
  onSelect: (id: number) => void;
  page: number;
  total: number;
  onPage: (page: number) => void;
  loading: boolean;
  error: string | null;
}) {
  const { companyId } = useCompanyId();
  const [filtersAnchor, setFiltersAnchor] = useState<HTMLElement | null>(null);
  const queueFiltered = !(queue in TAB_LABELS);
  const active = [queueFiltered && QUEUE_LABELS[queue], scope !== 'anyone' && SCOPE_LABELS[scope]].filter(Boolean);
  const pick = (next: { queue?: InquiryQueueFilter; scope?: InquiryScope }) => { setFiltersAnchor(null); onFilters(next); };
  const selectedRow = useRef<HTMLDivElement>(null);

  useEffect(() => {
    selectedRow.current?.scrollIntoView?.({ block: 'nearest' });
  }, [items, selectedId]);

  return (
    <Paper
      elevation={0}
      sx={{
        flex: { lg: '0 0 clamp(300px, 25%, 400px)' },
        minWidth: { lg: 300 },
        height: { xs: '70vh', lg: 'auto' },
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${INK.border}`,
        borderRadius: '10px',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: '18px 16px 12px', display: 'flex', flexDirection: 'column', gap: '10px', borderBottom: `1px solid ${INK.border}`, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PpLogo ppType="paypal" size={18} />
            <Typography component="h1" sx={{ fontSize: 18, fontWeight: 600, color: INK.text }}>Inquiries</Typography>
          </Box>
          <Typography component="span" sx={{ fontSize: 12, color: INK.muted }}>{total} {queue === 'all' || queue === 'closed' || search.trim() ? 'inquiries' : 'waiting'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Box role="tablist" sx={{ display: 'flex', gap: 2, borderBottom: `1px solid ${INK.border}`, flex: 1, minWidth: 0 }}>
            {TABS.map((q) => (
              <Box
                key={q}
                component="button"
                type="button"
                role="tab"
                aria-selected={q === queue}
                onClick={() => onFilters({ queue: q })}
                sx={{
                  p: '0 0 8px', mb: '-1px', border: 0, borderBottom: '2px solid transparent', bgcolor: 'transparent', cursor: 'pointer',
                  font: 'inherit', fontSize: 13, color: INK.muted, whiteSpace: 'nowrap',
                  ...(q === queue && { color: INK.primary, borderBottomColor: INK.primary, fontWeight: 600 }),
                }}
              >
                {TAB_LABELS[q]}
                {counts[q] > 0 && <> <Box component="span" sx={{ fontWeight: 500, opacity: 0.8 }}>{countOf(q, counts)}</Box></>}
              </Box>
            ))}
          </Box>
          <IconButton
            aria-label="Filters"
            aria-haspopup="menu"
            onClick={(e) => setFiltersAnchor(e.currentTarget)}
            sx={{ position: 'relative', width: 28, height: 28, flexShrink: 0, border: `1px solid ${INK.control}`, borderRadius: '8px', color: INK.body }}
          >
            <FilterListOutlined sx={{ fontSize: 16 }} />
            {(queueFiltered || scope !== 'anyone') && (
              <Box data-testid="filters-dot" sx={{ position: 'absolute', top: -4, right: -4, width: 8, height: 8, borderRadius: '50%', bgcolor: INK.primary, border: '2px solid #fff', boxSizing: 'content-box' }} />
            )}
          </IconButton>
          <Menu anchorEl={filtersAnchor} open={!!filtersAnchor} onClose={() => setFiltersAnchor(null)} slotProps={{ paper: { sx: { width: 200 } } }}>
            <ListSubheader sx={MENU_HEADER}>Queue</ListSubheader>
            {INQUIRY_QUEUE_FILTERS.map((q) => (
              <MenuItem key={q} selected={q === queue} onClick={() => pick({ queue: q })} sx={MENU_ITEM}>
                {QUEUE_LABELS[q]}<MenuCount count={countOf(q, counts)} />
              </MenuItem>
            ))}
            <Divider />
            <ListSubheader sx={MENU_HEADER}>Show</ListSubheader>
            {INQUIRY_SCOPES.map((s) => (
              <MenuItem key={s} selected={s === scope} onClick={() => pick({ scope: s })} sx={MENU_ITEM}>
                {SCOPE_LABELS[s]}<MenuCount count={countOf(s, counts)} />
              </MenuItem>
            ))}
          </Menu>
        </Box>
        {active.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 11, color: INK.primary }}>
            Showing <b>{active.join(' · ')}</b>
            <IconButton aria-label="Reset filters" size="small" onClick={() => onFilters({ queue: 'needs_reply', scope: 'anyone' })} sx={{ p: 0, fontSize: 11, color: INK.muted }}>×</IconButton>
          </Box>
        )}
        <TextField
          size="small"
          fullWidth
          placeholder="Name, email, case or order #"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          slotProps={{ input: { endAdornment: search && (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="Clear search" onClick={() => onSearch('')} edge="end">
                <CloseOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </InputAdornment>
          ) } }}
          sx={{ '& .MuiInputBase-root': { height: 36, fontSize: 13 } }}
        />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {error ? (
          <Alert severity="error" sx={{ m: 1.5 }}>{error}</Alert>
        ) : loading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>
        ) : items.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: INK.muted, p: 3, textAlign: 'center' }}>No inquiries</Typography>
        ) : (
          <List disablePadding>
            {items.map((item) => {
              const hoursLeft = (item.queue === 'needs_reply' || item.proofRequested) && item.sellerDueAt ? dayjs(item.sellerDueAt).diff(dayjs(), "hour", true) : null;
              return (
                <ListItemButton
                  key={item.disputeId}
                  ref={item.disputeId === selectedId ? selectedRow : undefined}
                  selected={item.disputeId === selectedId}
                  onClick={() => onSelect(item.disputeId)}
                  disableRipple
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: '3px',
                    p: '10px 14px',
                    borderBottom: `1px solid ${INK.divider}`,
                    '&:hover': { bgcolor: INK.thread },
                    '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': { bgcolor: INK.tint },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, fontSize: 13, fontWeight: 600 }}>
                    <Box component="span" sx={ELLIPSIS}>
                      {item.buyerName}
                      {companyId === null && <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: '#98a2b3' }}> · {item.companyName}</Box>}
                    </Box>
                    {hoursLeft !== null && (
                      <Box component="span" sx={{ flexShrink: 0, fontSize: 12, color: timeLeftColor(hoursLeft) }}>{formatTimeLeft(hoursLeft)}</Box>
                    )}
                  </Box>
                  <Box sx={{ fontSize: 12, color: INK.muted, ...ELLIPSIS }}>
                    <Box component="span" sx={{ color: INK.body, fontWeight: 500 }}>{formatCurrency(item.amount, item.currency)}</Box>
                    {[reasonLabel(item.reason), item.brandName !== item.companyName && item.brandName, item.waitingSince && `waiting ${formatWaiting(dayjs().diff(dayjs(item.waitingSince), "hour", true))}`].filter(Boolean).map((part) => ` · ${part}`).join("")}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                    {item.proofRequested || (item.evidenceSentAt && item.queue !== 'closed') ? (
                      <Box component="span" sx={{ ...PILL, ...PROOF_COLORS[item.proofRequested ? 'requested' : 'sent'] }}>
                        {item.proofRequested ? 'Proof requested' : 'Evidence sent'}
                      </Box>
                    ) : item.status && (queue === 'all' || queue === 'closed' || scope !== 'anyone' || item.status === 'UNDER_REVIEW') && (
                      <Box component="span" sx={{ ...PILL, ...statusChipColors(item) }}>
                        {paypalStatusLabel(item.status)}
                      </Box>
                    )}
                    <Chip size="small" variant="outlined" label={item.assignedFirstName ?? 'Unassigned'} sx={{ height: 16, fontSize: 10, borderRadius: '8px', flexShrink: 0, '& .MuiChip-label': { px: '6px' }, ...(!item.assignedFirstName && { color: INK.muted }) }} />
                    {item.lastMessagePreview && (
                      <Box component="span" sx={{ fontSize: 12, color: INK.muted, minWidth: 0, ...ELLIPSIS }}>
                        {`${item.lastMessageBy ? `${AUTHOR_PREFIX[item.lastMessageBy]}: ` : ''}${item.lastMessagePreview}`}
                      </Box>
                    )}
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={25}
        rowsPerPageOptions={[]}
        onPageChange={(_, next) => onPage(next)}
        sx={{ flexShrink: 0, borderTop: `1px solid ${INK.border}`, '& .MuiTablePagination-toolbar': { minHeight: 44 }, '& .MuiTablePagination-displayedRows': { fontSize: 12, color: INK.muted } }}
      />
    </Paper>
  );
}
